import __vue_component__ from '../Form/TextField.vue.js';
import __vue_component__$1 from '../Popper/Popper.vue.js';
import __vue_component__$2 from './SelectMenu.vue.js';
import __vue_component__$3 from './Tag.vue.js';
import __vue_component__$4 from './Icons.vue.js';

//
const INPUT_WIDTH = '5px';
var script = {
  components: {
    TextField: __vue_component__,
    Popper: __vue_component__$1,
    SelectMenu: __vue_component__$2,
    Tag: __vue_component__$3,
    Icons: __vue_component__$4
  },
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Type to search...'
    },
    searchPromptText: {
      type: String,
      default: 'Type to search...'
    },
    multi: {
      type: Boolean,
      default: false
    },
    filterPredicate: {
      type: Function,
      default: (label = '', input = '') => label.toString().toLowerCase().includes(input.toLowerCase().trim())
    },
    normalizer: {
      type: Function,
      default: value => ({
        id: value,
        label: value,
        value,
        disabled: false
      })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    isFocused: {
      type: Boolean,
      default: false
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    async: {
      type: Boolean,
      default: false
    },
    boundariesElement: {
      type: String,
      default: 'viewport'
    },
    noOptionsMessage: {
      type: String,
      default: 'No options'
    },
    createable: {
      type: Boolean,
      default: false
    },
    isClearable: {
      type: Boolean,
      default: true
    },
    isValidOption: {
      type: Function,
      default: () => true
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: Infinity
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    select: {
      type: Boolean,
      default: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    openOnFocus: {
      type: Boolean,
      default: false
    },
    confirm: {
      type: Boolean,
      default: true
    },
    dropdownWidth: {
      type: Number,
      default: undefined
    }
  },

  data() {
    return {
      isOpen: false,
      search: '',
      focused: false,
      currentSuggestionIndex: undefined,
      currentWidth: INPUT_WIDTH,
      isDirty: false,
      selectWidth: 'auto',
      draggedElement: undefined,
      prevIndex: undefined,
      nextIndex: undefined
    };
  },

  computed: {
    selected() {
      return this.multi ? this.value.map(e => this.normalizer(e)) : this.normalizer(this.value);
    },

    normalizedOptions() {
      return this.options.map(e => this.normalizer(e));
    },

    input() {
      return this.search ? '' : this.selected.label || this.placeholder;
    },

    nonSelectedSuggestions() {
      return this.multi ? this.normalizedOptions.filter(option => !this.selected.find(selected => option.id === selected.id)) : this.normalizedOptions;
    },

    suggestions() {
      if (this.search && !this.async) {
        return this.nonSelectedSuggestions.filter(option => this.filterPredicate(option.label, this.search));
      }

      return this.nonSelectedSuggestions;
    },

    hasSuggestions() {
      return this.suggestions && this.suggestions.length > 0;
    },

    isAnyOptionSelected() {
      return this.multi && !!this.selected.filter(o => !o.disabled).length || !!this.selected.value;
    },

    disabled() {
      return this.multi && this.selected.filter(o => o.disabled).map(o => o.value);
    },

    shouldBackspaceRemoveOption() {
      return !this.isClearable || this.multi && this.selected.length === 0;
    },

    canRemoveLastTag() {
      return !this.search && this.selected.length > this.min;
    },

    canClearSelectedOption() {
      return !this.search && !this.multi && this.selected;
    },

    canCreateTag() {
      return this.createable && this.search && this.selected.length < this.max;
    },

    shouldOpenMenu() {
      return this.isOpen && !this.isDirty && !this.createable;
    },

    nonClearableOptions() {
      if (this.multi) {
        const min = this.selected.slice(0, this.min).map(o => o.value);
        return [...new Set([...this.disabled, ...min])];
      }

      return undefined;
    },

    showClearIcon() {
      if (this.multi) {
        return this.selected.length > this.min;
      }

      return this.isClearable;
    }

  },
  watch: {
    isFocused: {
      handler(isFocused) {
        if (isFocused) {
          this.$nextTick(() => {
            if (this.$refs.input) {
              this.$refs.input.focus();
            }
          });
        }
      },

      immediate: true
    },

    search() {
      if (!this.search) this.currentWidth = INPUT_WIDTH;
      if (this.async && this.search) this.isDirty = true;
      this.$emit('search-change', this.search);
    },

    isOpen(open) {
      if (!open) {
        this.currentSuggestionIndex = undefined;
        this.$emit('close');
      } else {
        const width = this.dropdownWidth || this.$refs.target.getBoundingClientRect().width;
        this.selectWidth = `${width}px`;
        this.$emit('open');
      }
    },

    isFetching(isFetching) {
      if (!isFetching) {
        this.isDirty = false;
      }
    },

    isLoading(next, prev) {
      if (!next && prev && this.$refs.input) {
        this.$refs.input.blur();
      }
    },

    suggestions() {
      this.$nextTick(() => this.updatePopperPosition());
    }

  },
  methods: {
    onFocus(e) {
      if (this.isLoading) return;
      this.focused = true;

      if (this.openOnFocus && this.$refs.target && !this.$refs.target.contains(e.relatedTarget)) {
        this.isOpen = true;
      }

      this.$emit('focus', e);
    },

    onBlur(e) {
      if (this.$refs.target && !this.$refs.target.contains(e.relatedTarget)) {
        if (this.canCreateTag) {
          this.createTag();
        }

        this.search = '';
        this.closeOptions();
        this.$emit('blur', e);
      }
    },

    click() {
      this.isOpen = !this.isOpen;
      this.$refs.input.focus();
    },

    onEsc() {
      this.isOpen = false;
      this.$emit('cancel');
    },

    closeOptions() {
      this.isOpen = false;
      this.focused = false;
    },

    onClear() {
      this.$emit('input', this.nonClearableOptions);
      this.isOpen = false;
      this.$nextTick(() => this.$refs.input.focus());
    },

    onOptionSelected(option) {
      this.search = '';
      this.isOpen = false;
      this.focused = true;
      const selected = this.multi ? [...this.selected.map(e => e.value), option.value] : option.value;
      this.$emit('input', selected);

      if (!this.confirm && this.$refs.input) {
        this.$nextTick(() => this.$refs.input.blur());
      }
    },

    onInput({
      target
    }) {
      this.search = target.value;
      this.isOpen = true;
      this.resize();
      this.updatePopperPosition();
    },

    onRemove(id) {
      if (!this.selected.length) return;
      const selected = this.selected.filter(option => option.id !== id || option.disabled).map(option => option.value);
      this.updatePopperPosition();
      this.$emit('input', selected);
      this.$nextTick(() => this.updatePopperPosition());
    },

    removeOption() {
      if (this.shouldBackspaceRemoveOption) return;

      if (this.canRemoveLastTag) {
        const {
          id
        } = this.selected[this.selected.length - 1];
        this.onRemove(id);
      } else if (this.canClearSelectedOption) {
        this.$emit('input', undefined);
      }
    },

    onNextSuggestion() {
      if (!this.isOpen) this.isOpen = true;

      if (!this.hasSuggestions) {
        this.currentSuggestionIndex = undefined;
        return;
      }

      if (this.currentSuggestionIndex === undefined) {
        this.currentSuggestionIndex = 0;
      } else {
        this.currentSuggestionIndex += 1;

        if (this.currentSuggestionIndex > this.suggestions.length - 1) {
          this.currentSuggestionIndex = 0;
        }
      }
    },

    onPreviousSuggestion() {
      if (!this.isOpen) this.isOpen = true;

      if (!this.hasSuggestions) {
        this.currentSuggestionIndex = undefined;
        return;
      }

      if (this.currentSuggestionIndex === undefined) {
        this.currentSuggestionIndex = this.suggestions.length - 1;
      } else {
        this.currentSuggestionIndex -= 1;

        if (this.currentSuggestionIndex < 0) {
          this.currentSuggestionIndex = this.suggestions.length - 1;
        }
      }
    },

    onMouseOverSuggestion(id) {
      this.currentSuggestionIndex = id;
    },

    onSuggestionSelected(e) {
      // if suggested option is undefined, means we don't want to select any value, just submit
      const suggestedOption = this.suggestions && this.suggestions[this.currentSuggestionIndex];

      if (suggestedOption === undefined && !this.canCreateTag) {
        this.$emit('confirm', e);
        return;
      }

      if (this.canCreateTag) {
        if (!this.isValidOption(this.search)) {
          this.$emit('error');
          return;
        }

        this.createTag();
      }

      e.preventDefault();

      if (!this.hasSuggestions && this.isOpen) {
        return;
      }

      this.currentSuggestionIndex = undefined;
      this.$nextTick(() => {
        this.$refs.input.focus();
        this.onOptionSelected(suggestedOption);
      });
    },

    resize() {
      this.$nextTick(() => {
        if (this.$refs.input) {
          this.currentWidth = `${this.$refs.input.scrollWidth}px`;
        }
      });
    },

    updatePopperPosition() {
      if (this.$refs.menu) {
        this.$refs.menu.update();
      }
    },

    createTag() {
      const selected = this.multi ? [...this.selected.map(o => o.value), this.search] : this.search;
      this.search = '';
      this.$emit('input', selected);
    },

    handleDrag(e) {
      const x = e.clientX;
      const y = e.clientY;
      this.draggedElement.classList.add('ghost');
      const el = document.elementFromPoint(x, y);
      let swapItem = el === null ? this.draggedElement : el.closest('[draggable="true"]');

      if (swapItem) {
        swapItem = swapItem !== this.draggedElement.nextSibling ? swapItem : swapItem.nextSibling;
        this.$refs.list.insertBefore(this.draggedElement, swapItem);
      }
    },

    onDragEnd() {
      this.dragging = false;
      const nextIndex = [...this.$refs.list.children].indexOf(this.draggedElement);
      this.draggedElement.classList.remove('ghost');
      const list = [...this.selected];
      const [item] = list.splice(this.prevIndex, 1);
      list.splice(nextIndex, 0, item);
      this.$emit('input', list.map(e => e.value));
      this.$refs.input.focus();
    },

    onDragStart(e, index) {
      this.dragging = true;
      this.isOpen = false;
      this.draggedElement = e.target;
      this.prevIndex = index;
    }

  }
};

export default script;



// WEBPACK FOOTER //
// ./node_modules/@spartez/vue-atlaskit/dist/components/Select/Select.vue_rollup-plugin-vue=script.js